import React, { useState, useEffect, useRef } from "react";
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Paper,
  CircularProgress, Chip, IconButton, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, Tooltip, Alert, Snackbar, Accordion, AccordionSummary,
  AccordionDetails, Switch, FormControlLabel, Divider, Select, MenuItem,
} from "@mui/material";
import { Check, Close, Delete, Edit, Visibility, Refresh, Search, ExpandMore, Add, MeetingRoom, PhotoCamera } from "@mui/icons-material";
import axios from "axios";
import { api_urls } from "../../../utils/api-urls";

const API = api_urls + "api/sanatani-hotel";
const IMG_BASE = "https://api.bharatdarshan.app";

const SSProperties = () => {
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [editDialog, setEditDialog] = useState(false);
  const [editProp, setEditProp] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [detailProp, setDetailProp] = useState(null);
  const [roomDialog, setRoomDialog] = useState(false);
  const [roomPropId, setRoomPropId] = useState(null);
  const [roomForm, setRoomForm] = useState({ roomType: "Standard", roomName: "", maxOccupancy: 2, totalRooms: 1, basePrice: 500 });
  const [editRoomIdx, setEditRoomIdx] = useState(-1);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });
  const [search, setSearch] = useState("");
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchProperties(); }, []);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API + "/admin/properties");
      if (res.data?.success) setProperties(res.data.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleAction = async (id, action) => {
    try {
      await axios.put(API + "/admin/property/" + id + "/" + action);
      setSnack({ open: true, msg: "Property " + action + "d!", severity: "success" });
      fetchProperties();
    } catch (e) { setSnack({ open: true, msg: "Error", severity: "error" }); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(API + "/admin/property/" + deleteId);
      setSnack({ open: true, msg: "Property deleted!", severity: "success" });
      setDeleteDialog(false);
      fetchProperties();
    } catch (e) { setSnack({ open: true, msg: "Error deleting", severity: "error" }); }
  };

  const handleEditSave = async () => {
    try {
      await axios.put(API + "/admin/property/" + editProp._id + "/edit", editProp);
      setSnack({ open: true, msg: "Property updated!", severity: "success" });
      setEditDialog(false);
      fetchProperties();
    } catch (e) { setSnack({ open: true, msg: "Error updating", severity: "error" }); }
  };

  const handleViewDetail = async (id) => {
    try {
      const res = await axios.get(API + "/admin/property/" + id + "/details");
      if (res.data?.success) { setDetailProp(res.data.data); setDetailDialog(true); }
    } catch (e) { console.error(e); }
  };

  const handleAddRoom = async () => {
    try {
      await axios.post(API + "/admin/property/" + roomPropId + "/room", roomForm);
      setSnack({ open: true, msg: "Room added!", severity: "success" });
      setRoomDialog(false);
      setRoomForm({ roomType: "Standard", roomName: "", maxOccupancy: 2, totalRooms: 1, basePrice: 500 });
      fetchProperties();
    } catch (e) { setSnack({ open: true, msg: "Error adding room", severity: "error" }); }
  };

  const handleEditRoom = async () => {
    try {
      await axios.put(API + "/admin/property/" + roomPropId + "/room/" + editRoomIdx, roomForm);
      setSnack({ open: true, msg: "Room updated!", severity: "success" });
      setRoomDialog(false);
      setEditRoomIdx(-1);
      fetchProperties();
    } catch (e) { setSnack({ open: true, msg: "Error updating room", severity: "error" }); }
  };

  const handleDeleteRoom = async (propId, roomIdx) => {
    if (!window.confirm("Delete this room?")) return;
    try {
      await axios.delete(API + "/admin/property/" + propId + "/room/" + roomIdx);
      setSnack({ open: true, msg: "Room deleted!", severity: "success" });
      fetchProperties();
    } catch (e) { setSnack({ open: true, msg: "Error deleting room", severity: "error" }); }
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !editProp) return;
    setImageUploading(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach(f => formData.append('images', f));
      const res = await axios.post(API + "/admin/property/" + editProp._id + "/upload-images", formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.success) {
        setEditProp(prev => ({ ...prev, images: res.data.data }));
        setSnack({ open: true, msg: "Images uploaded!", severity: "success" });
      }
    } catch (e) {
      setSnack({ open: true, msg: "Image upload failed", severity: "error" });
    } finally {
      setImageUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteImage = async (imgIdx) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      const res = await axios.delete(API + "/admin/property/" + editProp._id + "/image/" + imgIdx);
      if (res.data?.success) {
        setEditProp(prev => ({ ...prev, images: res.data.data }));
        setSnack({ open: true, msg: "Image deleted!", severity: "success" });
      }
    } catch (e) {
      setSnack({ open: true, msg: "Error deleting image", severity: "error" });
    }
  };

  const filtered = properties.filter(p => 
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) || 
    p.address?.city?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>;

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>🏨 Properties ({properties.length})</Typography>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchProperties}>Refresh</Button>
      </Box>

      <TextField size="small" placeholder="Search by name or city..." value={search} onChange={e => setSearch(e.target.value)}
        InputProps={{ startAdornment: <Search sx={{ color: "#999", mr: 1 }} /> }} sx={{ minWidth: 300, mb: 2 }} />

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell><b>#</b></TableCell>
              <TableCell><b>Name</b></TableCell>
              <TableCell><b>Type</b></TableCell>
              <TableCell><b>City</b></TableCell>
              <TableCell><b>Partner</b></TableCell>
              <TableCell><b>Rooms</b></TableCell>
              <TableCell><b>Price Range</b></TableCell>
              <TableCell><b>Status</b></TableCell>
              <TableCell><b>Actions</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((p, i) => {
              const prices = (p.rooms || []).map(r => r.basePrice || r.pricePerNight || 0);
              const minP = prices.length ? Math.min(...prices) : 0;
              const maxP = prices.length ? Math.max(...prices) : 0;
              return (
                <TableRow key={p._id} hover>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell>
                    <Typography fontWeight={600} fontSize={13}>{p.name}</Typography>
                    {p.nameHindi && <Typography variant="caption" color="textSecondary">{p.nameHindi}</Typography>}
                  </TableCell>
                  <TableCell><Chip label={p.propertyType} size="small" variant="outlined" sx={{ textTransform: "capitalize" }} /></TableCell>
                  <TableCell>{p.address?.city || "-"}, {p.address?.state || ""}</TableCell>
                  <TableCell>{p.partnerId?.businessName || "-"}</TableCell>
                  <TableCell>
                    <Chip icon={<MeetingRoom fontSize="small" />} label={(p.rooms || []).length + " types"} size="small" />
                  </TableCell>
                  <TableCell>₹{minP} - ₹{maxP}</TableCell>
                  <TableCell>
                    <Chip label={p.isApproved ? "Approved" : "Pending"} size="small" color={p.isApproved ? "success" : "warning"} />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="View"><IconButton size="small" onClick={() => handleViewDetail(p._id)}><Visibility fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => { setEditProp({...p}); setEditDialog(true); }}><Edit fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Add Room"><IconButton size="small" color="info" onClick={() => { setRoomPropId(p._id); setEditRoomIdx(-1); setRoomForm({ roomType: "Standard", roomName: "", maxOccupancy: 2, totalRooms: 1, basePrice: 500 }); setRoomDialog(true); }}><Add fontSize="small" /></IconButton></Tooltip>
                      {!p.isApproved && <Tooltip title="Approve"><IconButton size="small" color="success" onClick={() => handleAction(p._id, "approve")}><Check fontSize="small" /></IconButton></Tooltip>}
                      {p.isApproved && <Tooltip title="Reject"><IconButton size="small" color="warning" onClick={() => handleAction(p._id, "reject")}><Close fontSize="small" /></IconButton></Tooltip>}
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => { setDeleteId(p._id); setDeleteDialog(true); }}><Delete fontSize="small" /></IconButton></Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {filtered.length === 0 && <Box py={4} textAlign="center"><Typography color="textSecondary">No properties found</Typography></Box>}
      </Paper>

      {/* Edit Property Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Property</DialogTitle>
        <DialogContent>
          {editProp && (
            <Grid container spacing={2} mt={0.5}>
              <Grid item xs={6}><TextField fullWidth label="Name" value={editProp.name || ""} onChange={e => setEditProp({...editProp, name: e.target.value})} /></Grid>
              <Grid item xs={6}><TextField fullWidth label="Name Hindi" value={editProp.nameHindi || ""} onChange={e => setEditProp({...editProp, nameHindi: e.target.value})} /></Grid>
              <Grid item xs={4}><TextField fullWidth label="City" value={editProp.address?.city || ""} onChange={e => setEditProp({...editProp, address: {...(editProp.address||{}), city: e.target.value}})} /></Grid>
              <Grid item xs={4}><TextField fullWidth label="State" value={editProp.address?.state || ""} onChange={e => setEditProp({...editProp, address: {...(editProp.address||{}), state: e.target.value}})} /></Grid>
              <Grid item xs={4}><TextField fullWidth label="Pincode" value={editProp.address?.pincode || ""} onChange={e => setEditProp({...editProp, address: {...(editProp.address||{}), pincode: e.target.value}})} /></Grid>
              <Grid item xs={4}><TextField fullWidth label="Property Type" value={editProp.propertyType || ""} onChange={e => setEditProp({...editProp, propertyType: e.target.value})} /></Grid>
              <Grid item xs={4}><TextField fullWidth label="Contact Phone" value={editProp.contactPhone || ""} onChange={e => setEditProp({...editProp, contactPhone: e.target.value})} /></Grid>
              <Grid item xs={4}><TextField fullWidth label="Star Rating" type="number" value={editProp.starRating || 0} onChange={e => setEditProp({...editProp, starRating: parseInt(e.target.value)})} /></Grid>
              <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Description" value={editProp.description || ""} onChange={e => setEditProp({...editProp, description: e.target.value})} /></Grid>
              <Grid item xs={12}>
                <FormControlLabel control={<Switch checked={editProp.isVegOnly || false} onChange={e => setEditProp({...editProp, isVegOnly: e.target.checked})} />} label="Veg Only" />
                <FormControlLabel control={<Switch checked={editProp.isFeatured || false} onChange={e => setEditProp({...editProp, isFeatured: e.target.checked})} />} label="Featured" />
              </Grid>

              {/* Property Images */}
              <Grid item xs={12}>
                <Divider sx={{ mb: 2 }} />
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography fontWeight={700}>🖼️ Property Images ({(editProp.images||[]).length})</Typography>
                  <Box>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      onChange={handleImageUpload}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={imageUploading ? <CircularProgress size={14} /> : <PhotoCamera />}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={imageUploading}
                    >
                      {imageUploading ? "Uploading..." : "Upload Images"}
                    </Button>
                  </Box>
                </Box>
                {(editProp.images || []).length === 0 ? (
                  <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>No images uploaded yet</Typography>
                ) : (
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {(editProp.images || []).map((img, imgIdx) => (
                      <Box key={imgIdx} sx={{ position: 'relative', width: 100, height: 80 }}>
                        <img
                          src={IMG_BASE + img.url}
                          alt={`Property ${imgIdx + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6, border: img.isMain ? '2px solid #FF6B00' : '1px solid #ddd' }}
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                        {img.isMain && (
                          <Chip label="Main" size="small" sx={{ position: 'absolute', top: 2, left: 2, fontSize: 9, height: 16, backgroundColor: '#FF6B00', color: '#fff' }} />
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteImage(imgIdx)}
                          sx={{ position: 'absolute', top: -4, right: -4, backgroundColor: '#fff', border: '1px solid #ddd', '&:hover': { backgroundColor: '#ffebee' }, padding: '2px' }}
                        >
                          <Close fontSize="small" sx={{ fontSize: 12, color: '#C62828' }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Grid>

              {/* Room Management */}
              <Grid item xs={12}>
                <Divider sx={{ mb: 1 }} />
                <Typography fontWeight={700} mb={1}>🛏️ Rooms ({(editProp.rooms||[]).length})</Typography>
                {(editProp.rooms || []).map((r, idx) => (
                  <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, p: 1, backgroundColor: "#f9f9f9", borderRadius: 1 }}>
                    <Typography fontSize={13} flex={1}>{r.roomType || r.roomName} - ₹{r.basePrice || r.pricePerNight}/night - {r.totalRooms} rooms - Max {r.maxOccupancy} guests</Typography>
                    <IconButton size="small" color="primary" onClick={() => { setRoomPropId(editProp._id); setEditRoomIdx(idx); setRoomForm({...r, basePrice: r.basePrice || r.pricePerNight || 500}); setRoomDialog(true); setEditDialog(false); }}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteRoom(editProp._id, idx)}><Delete fontSize="small" /></IconButton>
                  </Box>
                ))}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setEditDialog(false)}>Cancel</Button><Button variant="contained" onClick={handleEditSave}>Save</Button></DialogActions>
      </Dialog>

      {/* Room Add/Edit Dialog */}
      <Dialog open={roomDialog} onClose={() => setRoomDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editRoomIdx >= 0 ? "Edit Room" : "Add Room"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={6}><TextField fullWidth label="Room Type" value={roomForm.roomType || ""} onChange={e => setRoomForm({...roomForm, roomType: e.target.value})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Room Name" value={roomForm.roomName || ""} onChange={e => setRoomForm({...roomForm, roomName: e.target.value})} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Max Occupancy" type="number" value={roomForm.maxOccupancy || 2} onChange={e => setRoomForm({...roomForm, maxOccupancy: parseInt(e.target.value)})} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Total Rooms" type="number" value={roomForm.totalRooms || 1} onChange={e => setRoomForm({...roomForm, totalRooms: parseInt(e.target.value)})} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Price/Night" type="number" value={roomForm.basePrice || 500} onChange={e => setRoomForm({...roomForm, basePrice: parseInt(e.target.value)})} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions><Button onClick={() => setRoomDialog(false)}>Cancel</Button><Button variant="contained" onClick={editRoomIdx >= 0 ? handleEditRoom : handleAddRoom}>{editRoomIdx >= 0 ? "Update" : "Add"}</Button></DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>⚠️ Delete Property</DialogTitle>
        <DialogContent><Typography>This will permanently delete the property and all its bookings. Continue?</Typography></DialogContent>
        <DialogActions><Button onClick={() => setDeleteDialog(false)}>Cancel</Button><Button variant="contained" color="error" onClick={handleDelete}>Delete</Button></DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Property Details</DialogTitle>
        <DialogContent>
          {detailProp && (
            <Box>
              <Typography variant="h5" fontWeight={700}>{detailProp.name}</Typography>
              {detailProp.nameHindi && <Typography color="textSecondary">{detailProp.nameHindi}</Typography>}
              <Chip label={detailProp.propertyType} sx={{ mt: 1, mb: 2, textTransform: "capitalize" }} />
              {(detailProp.images || []).length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                  {(detailProp.images || []).map((img, ii) => (
                    <img key={ii} src={IMG_BASE + img.url} alt="" style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 6, border: img.isMain ? '2px solid #FF6B00' : '1px solid #ddd' }} />
                  ))}
                </Box>
              )}
              <Grid container spacing={2}>
                <Grid item xs={6}><Typography variant="body2" color="textSecondary">Location</Typography><Typography>{[detailProp.address?.street, detailProp.address?.city, detailProp.address?.state, detailProp.address?.pincode].filter(Boolean).join(", ")}</Typography></Grid>
                <Grid item xs={3}><Typography variant="body2" color="textSecondary">Rating</Typography><Typography>⭐ {detailProp.starRating || 0}</Typography></Grid>
                <Grid item xs={3}><Typography variant="body2" color="textSecondary">Partner</Typography><Typography fontWeight={600}>{detailProp.partnerId?.businessName || "-"}</Typography></Grid>
                <Grid item xs={12}><Typography variant="body2" color="textSecondary">Description</Typography><Typography fontSize={13}>{detailProp.description || "-"}</Typography></Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography fontWeight={700} mb={1}>🛏️ Rooms ({(detailProp.rooms||[]).length})</Typography>
                  {(detailProp.rooms || []).map((r, ri) => (
                    <Box key={ri} sx={{ p: 1.5, mb: 1, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                      <Typography fontWeight={600}>{r.roomType || r.roomName} - ₹{r.basePrice || r.pricePerNight}/night</Typography>
                      <Typography variant="caption">Max {r.maxOccupancy} guests | {r.totalRooms} rooms available</Typography>
                    </Box>
                  ))}
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDetailDialog(false)}>Close</Button></DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({...snack, open: false})} anchorOrigin={{vertical:"top",horizontal:"right"}}>
        <Alert severity={snack.severity} onClose={() => setSnack({...snack, open: false})}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default SSProperties;
